import { useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, get, post, toastError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ModuleSelect } from "@/components/common/Pickers";
import { VoiceCapture } from "@/components/field/VoiceCapture";
import { DictateButton } from "@/components/field/DictateButton";
import { ISSUE_SEVERITIES, label } from "@/lib/labels";
import { fileSize } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Camera, Sparkles, Loader2, CheckCircle2, X, ArrowLeft, Bell, Eye, Home } from "lucide-react";
import "@/components/field/field.css";

const SEV_TONE = {
  lav: "border-foreground/30 bg-muted/60 text-foreground",
  middels: "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
  hoy: "border-orange-400 bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200",
  kritisk: "border-rose-400 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200",
};
const CATEGORIES = ["skade", "mangel", "hms", "forsinkelse", "kvalitet", "annet"];
const catLabel = (c) => (c === "hms" ? "HMS" : c.charAt(0).toUpperCase() + c.slice(1));
const EMPTY = { title: "", description: "", severity: "middels", category: "" };

const KiBadge = () => <span className="ki-badge" data-testid="field-issue-ki-badge"><Sparkles className="h-2.5 w-2.5" /> KI</span>;

/** Feltlabel med valgfritt KI-merke (feltet ble fylt av KI – kontroller og juster). */
const FieldLabel = ({ children, ki, right }) => (
  <div className="flex items-center justify-between gap-2 min-h-[20px]">
    <Label className="text-[13px] font-medium flex items-center gap-2">{children}{ki && <KiBadge />}</Label>
    {right}
  </div>
);

export default function FieldIssueNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [form, setForm] = useState({ ...EMPTY, project_id: params.get("project_id") || "", module_id: params.get("module_id") || "" });
  const [raw, setRaw] = useState("");
  const [files, setFiles] = useState([]);
  const [created, setCreated] = useState(null);
  const [ki, setKi] = useState({}); // hvilke felt KI fylte sist
  const [kiStamp, setKiStamp] = useState(0); // restarter «flash»
  const formRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const transportId = params.get("transport_id");
  const taskId = params.get("task_id");

  const today = useQuery({ queryKey: ["field-today"], queryFn: () => get("/field/today") });
  const projects = today.data?.projects || [];
  const selectedProject = projects.find((p) => p.id === form.project_id);

  const aiHelp = useMutation({
    mutationFn: () => post("/ai/issue-text", { text: raw, context: { prosjekt: selectedProject?.name, modul: form.module_id || undefined, transport: transportId || undefined } }),
    onSuccess: (d) => {
      const filled = {};
      setForm((f) => {
        const n = { ...f };
        if (d.title) { n.title = d.title; filled.title = true; }
        if (d.description) { n.description = d.description; filled.description = true; }
        if (ISSUE_SEVERITIES.includes(d.severity)) { n.severity = d.severity; filled.severity = true; }
        if (d.category && CATEGORIES.includes(d.category)) { n.category = d.category; filled.category = true; }
        return n;
      });
      setKi(filled); setKiStamp((s) => s + 1);
      toast.success("KI har fylt inn forslag – kontroller og juster før du sender");
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    },
    onError: (e) => toastError(e, "KI-assistenten er ikke tilgjengelig akkurat nå"),
  });

  const create = useMutation({
    mutationFn: async () => {
      const entity_type = transportId ? "transport" : form.module_id && !form.project_id ? "modul" : "prosjekt";
      const entity_id = transportId || (entity_type === "modul" ? form.module_id : form.project_id) || null;
      const issue = await post("/issues", { title: form.title.trim(), description: form.description.trim() || null, severity: form.severity, category: form.category || null, entity_type, entity_id, project_id: form.project_id || null, module_id: form.module_id || null });
      let uploaded = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "issue"); fd.append("entity_id", issue.id); fd.append("category", "bilde"); fd.append("title", f.name); fd.append("file", f);
        try { await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); uploaded += 1; } catch (e) { toastError(e, `Kunne ikke laste opp ${f.name}`); }
      }
      return { issue, uploaded };
    },
    onSuccess: ({ issue, uploaded }) => { setCreated({ ...issue, uploaded }); qc.invalidateQueries({ queryKey: ["issues"] }); qc.invalidateQueries({ queryKey: ["field-today"] }); window.scrollTo({ top: 0 }); },
    onError: (e) => toastError(e, "Kunne ikke registrere avviket"),
  });

  const resetAll = () => { setCreated(null); setForm({ ...EMPTY, project_id: form.project_id, module_id: "" }); setFiles([]); setRaw(""); setKi({}); };

  if (created) {
    return (
      <div className="space-y-4 fade-up" data-testid="field-issue-created">
        <div className="field-card p-6 text-center space-y-3">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary"><CheckCircle2 className="h-8 w-8" /></div>
          <h1 className="text-[22px] font-display font-semibold tracking-tight">Avvik registrert</h1>
          <div className="inline-flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-sm">Referanse <span className="font-mono font-semibold" data-testid="field-issue-number">{created.issue_number}</span></div>
          <p className="text-sm text-muted-foreground text-pretty">{created.title}{created.uploaded ? ` · ${created.uploaded} bilde${created.uploaded > 1 ? "r" : ""} lastet opp` : ""}.</p>
        </div>
        <div className="field-card p-4">
          <div className="text-[13px] font-semibold mb-2">Dette skjer nå</div>
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-start gap-3"><span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Bell className="h-3.5 w-3.5" /></span><div><div className="font-medium">Prosjektleder og flåte er varslet</div><div className="text-xs text-muted-foreground">Avviket ligger i oversikten med alvorlighet «{label(created.severity)}».</div></div></li>
            <li className="flex items-start gap-3"><span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Eye className="h-3.5 w-3.5" /></span><div><div className="font-medium">Du ser status på Hjem</div><div className="text-xs text-muted-foreground">Under «Mine avvik» til det er lukket.</div></div></li>
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="h-12 rounded-xl" onClick={resetAll} data-testid="field-issue-another-button">Nytt avvik</Button>
          <Button className="h-12 rounded-xl" onClick={() => navigate("/felt")} data-testid="field-issue-home-button"><Home className="h-4 w-4 mr-1.5" /> Til hjem</Button>
        </div>
      </div>
    );
  }

  const flash = (k) => (ki[k] ? "ki-flash" : "");

  return (
    <div className="space-y-4 fade-up" data-testid="field-issue-new-page">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground min-h-[44px]" data-testid="field-back-button"><ArrowLeft className="h-4 w-4" /> Tilbake</button>
      <div>
        <h1 className="text-[22px] font-display font-semibold tracking-tight flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Nytt avvik</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">Skade, mangel, HMS eller forsinkelse. Snakk inn eller skriv – legg ved bilder om du kan.</p>
      </div>

      <VoiceCapture
        value={raw}
        onChange={setRaw}
        canStructure={can("ai:write")}
        structuring={aiHelp.isPending}
        onStructure={() => aiHelp.mutate()}
        testId="field-issue-voice"
      />
      {(transportId || taskId) && <div className="flex flex-wrap gap-1.5">{transportId && <Badge variant="outline">Knyttet til transport</Badge>}{taskId && <Badge variant="outline">Fra oppgave</Badge>}</div>}

      <form ref={formRef} className="field-card p-4 space-y-4 scroll-mt-4" onSubmit={(e) => { e.preventDefault(); if (form.title.trim()) create.mutate(); }} data-testid="field-issue-form">
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold">Detaljer</h2>
          {Object.keys(ki).length > 0 && <span className="text-xs text-muted-foreground" data-testid="field-issue-ki-summary">Felt merket <span className="ki-badge align-middle"><Sparkles className="h-2.5 w-2.5" /> KI</span> er forslag – kontroller.</span>}
        </div>

        <div className="space-y-1.5">
          <FieldLabel>Prosjekt</FieldLabel>
          <Select value={form.project_id || "__none"} onValueChange={(v) => set("project_id", v === "__none" ? "" : v)}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="field-issue-project-select"><SelectValue placeholder="Velg prosjekt" /></SelectTrigger>
            <SelectContent><SelectItem value="__none">Ikke knyttet til prosjekt</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_number} – {p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Modul <span className="text-muted-foreground font-normal">(valgfritt)</span></FieldLabel>
          <ModuleSelect value={form.module_id || null} onChange={(v) => set("module_id", v || "")} testId="field-issue-module-select" className="h-11 rounded-xl" noneLabel="Ingen spesifikk modul" />
        </div>

        <div className="space-y-1.5" key={`t-${kiStamp}`}>
          <FieldLabel ki={ki.title}>Tittel <span className="text-destructive">*</span></FieldLabel>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={120} placeholder="Kort og konkret" className={cn("h-11 rounded-xl text-[15px]", flash("title"))} data-testid="field-issue-title-input" />
        </div>

        <div className="space-y-1.5" key={`s-${kiStamp}`}>
          <FieldLabel ki={ki.severity}>Alvorlighet</FieldLabel>
          <div className={cn("grid grid-cols-4 gap-1.5 rounded-xl", flash("severity"))} data-testid="field-issue-severity-group">
            {ISSUE_SEVERITIES.map((s) => (
              <button key={s} type="button" onClick={() => set("severity", s)} aria-pressed={form.severity === s} className={cn("rounded-xl border px-2 min-h-[44px] text-[13px] font-medium transition-colors duration-150", form.severity === s ? `${SEV_TONE[s]} ring-2 ring-primary/40` : "bg-card hover:bg-muted")} data-testid={`field-issue-severity-${s}`}>{label(s)}</button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5" key={`c-${kiStamp}`}>
          <FieldLabel ki={ki.category}>Kategori</FieldLabel>
          <div className={cn("flex flex-wrap gap-1.5 rounded-xl", flash("category"))}>
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => set("category", form.category === c ? "" : c)} aria-pressed={form.category === c} className={cn("rounded-full border px-3.5 min-h-[36px] text-[13px] transition-colors duration-150", form.category === c ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted")} data-testid={`field-issue-category-${c}`}>{catLabel(c)}</button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5" key={`d-${kiStamp}`}>
          <FieldLabel ki={ki.description} right={<DictateButton value={form.description} onChange={(v) => set("description", v)} testId="field-issue-description-dictate" label="Dikter beskrivelsen" />}>Beskrivelse</FieldLabel>
          <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Hva, hvor, konsekvens og foreslått tiltak" className={cn("rounded-xl text-[15px] leading-relaxed", flash("description"))} data-testid="field-issue-description-input" />
        </div>

        <div className="space-y-1.5">
          <FieldLabel>Bilder</FieldLabel>
          <div className="grid grid-cols-3 gap-2">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="relative aspect-square overflow-hidden rounded-xl border bg-muted/30" data-testid={`field-issue-photo-${i}`}>
                <img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" />
                <button type="button" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} className="absolute top-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 shadow" aria-label="Fjern bilde" data-testid={`field-issue-photo-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent px-2 pb-1.5 pt-4 text-[10px] text-white">{fileSize(f.size)}</div>
              </div>
            ))}
            <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed text-xs text-muted-foreground hover:bg-muted/50 active:bg-muted">
              <Camera className="h-5 w-5 text-primary" /> {files.length ? "Flere bilder" : "Ta bilde"}
              <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { setFiles((x) => [...x, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="field-issue-photo-input" />
            </label>
          </div>
        </div>

        <Button type="submit" className="w-full h-12 rounded-xl text-[15px]" disabled={!form.title.trim() || create.isPending} data-testid="field-issue-submit-button">{create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />} Registrer avvik</Button>
        <p className="text-center text-xs text-muted-foreground">Avviket blir synlig for prosjektleder og flåte umiddelbart. <Link to="/felt" className="underline underline-offset-2">Avbryt</Link></p>
      </form>
    </div>
  );
}
